import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Truck, Wrench, Headphones, Sparkles } from "lucide-react";
import { CONTACT, HEROES, CATEGORY_IMAGES } from "../data/gear";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12 } }),
};

const VIEWPORT_ONCE = { once: true };

export default function Home({ onQuote }) {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center">
        <img src={HEROES.home} alt="Emerald Coast sunset" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/10" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28">
          <motion.div initial="hidden" animate="show" variants={fade} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/80 mb-6">
              <Sparkles className="w-4 h-4" /> The Emerald Coast · 30A
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] tracking-tight text-balance">
              Elevate your coastal event.
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl leading-relaxed">
              Professional audio visual production gear and premium beach rentals — delivered, set up,
              and torn down for you across Santa Rosa Beach, Miramar Beach, Destin &amp; Panama City Beach.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="hero-quote-btn"
                onClick={() => onQuote()}
                className="px-8 py-4 rounded-full bg-accent text-accent-foreground font-semibold uppercase tracking-widest text-sm hover:bg-accent/90 transition-colors"
              >
                Request a Quote
              </button>
              <a
                href={CONTACT.phoneHref}
                data-testid="hero-call-btn"
                className="px-8 py-4 rounded-full border border-white/40 text-white font-semibold uppercase tracking-widest text-sm hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <motion.div initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fade} className="mb-14 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-accent mb-3">What we rent</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-primary tracking-tight">
            Two collections, one seamless experience
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          <CategoryCard
            to="/corporate-events"
            image="/home-corporate.jpg"
            eyebrow="Meetings & Conferences"
            title="Corporate Events"
            desc="Turn-key audio & video breakout packages for meetings, conferences and corporate sessions — installed and operated for you."
            testid="category-corporate-events"
          />
          <CategoryCard
            to="/corporate-av"
            image="/beach-audio-string-lights.jpg"
            eyebrow="AV Packages for Weddings"
            title="Wedding Packages"
            desc="Complete audio, video and lighting packages for waterfront weddings, ceremonies and receptions along the Emerald Coast."
            testid="category-wedding-packages"
          />
        </div>
      </section>

      {/* SERVICE / WHY */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="grid lg:grid-cols-3 gap-10">
            {[
              { icon: Truck, title: "Delivery to your door", desc: "We bring everything straight to your venue or beach access point." },
              { icon: Wrench, title: "Setup & teardown included", desc: "Our crew handles the heavy lifting before and after — every single time." },
              { icon: Headphones, title: "On-call event support", desc: "Local, friendly help so your sound and gear run flawlessly all event long." },
            ].map((f, i) => (
              <motion.div key={f.title} custom={i} initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fade} className="flex gap-4">
                <span className="shrink-0 flex items-center justify-center w-14 h-14 rounded-2xl bg-primary text-primary-foreground">
                  <f.icon className="w-6 h-6" />
                </span>
                <div>
                  <h3 className="font-heading text-2xl font-bold text-primary">{f.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-accent mb-3">Where we serve</p>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-primary tracking-tight mb-10">
          Proudly serving the Emerald Coast
        </h2>
        <div className="flex flex-wrap justify-center gap-3">
          {CONTACT.serviceAreas.map((a) => (
            <span key={a} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-border text-primary font-medium">
              <MapPin className="w-4 h-4 text-accent" /> {a}
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 sm:px-8 pb-24">
        <div className="max-w-6xl mx-auto rounded-3xl bg-primary text-primary-foreground px-8 sm:px-16 py-16 text-center relative overflow-hidden">
          <div className="relative">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4">Ready to plan your event?</h2>
            <p className="text-primary-foreground/80 max-w-xl mx-auto mb-8">
              Send us your details and we'll put together a custom quote — no obligation. Setup and teardown always included.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                data-testid="cta-quote-btn"
                onClick={() => onQuote()}
                className="px-8 py-4 rounded-full bg-accent text-accent-foreground font-semibold uppercase tracking-widest text-sm hover:bg-accent/90 transition-colors"
              >
                Request a Quote
              </button>
              <a href={CONTACT.phoneHref} className="px-8 py-4 rounded-full border border-white/30 font-semibold uppercase tracking-widest text-sm hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const CategoryCard = ({ to, image, eyebrow, title, desc, testid }) => (
  <Link to={to} data-testid={testid} className="group relative rounded-3xl overflow-hidden min-h-[420px] flex items-end bg-primary">
    <img src={image} alt={title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/20" />
    <div className="relative p-8 sm:p-10 text-white">
      <p className="text-xs uppercase tracking-[0.3em] text-accent mb-3">{eyebrow}</p>
      <h3 className="font-heading text-3xl sm:text-4xl font-bold mb-3">{title}</h3>
      <p className="text-sm text-white/80 max-w-md leading-relaxed">{desc}</p>
      <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest">
        Explore <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);
